import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Drawer, Icon, Tooltip, Skeleton, Dropdown, Menu } from 'antd';
import moment from 'moment';
import 'moment/locale/pt-br';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import * as documentsActions from '../actions';
import { ResendEmailModal } from '../Modals/ResendEmailModal';
import { EmailPreview } from '~/_domains/emailsLogs/components/EmailBodyPreviewModal';
import { deliveryMethodNames } from '~/_domains/customers/drawer/data';
import { department } from '~/components/RegisterTasks/Utils';
import { convertToBRL } from '~/utils/formatters';
import { copyToClipBoard } from '~/utils/copy-to-clipboard';
import {
  getTimelineEventMeta,
  getEmailEventMeta,
  getGatewayLabel,
  isTechnicalEvent,
  getDocumentHeadline,
  filterTimelineEvents,
  groupEventsByDay,
  getDocumentSituation,
  getDocumentJourney,
  formatIdentification,
  formatPhone,
} from './timeline-helpers';

// Paleta da gaveta: neutros frios + as cores de status da tabela.
var C = {
  ink: '#101828',
  text: '#344054',
  muted: '#667085',
  faint: '#98a2b3',
  line: '#eaecf0',
  soft: '#f9fafb',
  brand: '#1d4ed8',
  brandSoft: '#eff4ff',
  ok: '#039855',
  bad: '#d92d20',
  badSoft: '#fef3f2',
};

var RESENDABLE = ['draft', 'failed', 'bounced', 'blacklist'];

/* ---------- Estrutura ---------- */

var Shell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  color: ${C.text};
  font-size: 13px;
  font-feature-settings: 'tnum';
`;

var Header = styled.header`
  padding: 20px 28px 0;
  border-bottom: 1px solid ${C.line};
  background: linear-gradient(180deg, #ffffff 0%, ${C.soft} 100%);
`;

var HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

var FileTile = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${C.brandSoft};
  color: ${C.brand};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

var TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

var Title = styled.h2`
  margin: 0;
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
  color: ${C.ink};
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

var Subtitle = styled.div`
  margin-top: 2px;
  color: ${C.muted};
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

var HEADLINE_TONES = {
  ok: { color: '#027a48', background: '#ecfdf3', icon: 'check-circle' },
  pending: { color: '#175cd3', background: '#eff8ff', icon: 'clock-circle' },
  warn: { color: '#b54708', background: '#fffaeb', icon: 'exclamation-circle' },
  bad: { color: '#b42318', background: '#fef3f2', icon: 'close-circle' },
  neutral: { color: '#475467', background: '#f2f4f7', icon: 'inbox' },
};

var Headline = styled.div`
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 10px;
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: ${function(p) { return p.tone.color; }};
  background: ${function(p) { return p.tone.background; }};

  .sep { opacity: 0.4; }
`;

var Switch = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: none;
  padding: 0;
  cursor: pointer;
  color: ${C.muted};
  font-size: 12px;

  i.track {
    position: relative;
    width: 28px;
    height: 16px;
    border-radius: 999px;
    background: ${function(p) { return p.on ? C.brand : '#d0d5dd'; }};
    transition: background 0.15s;
  }

  i.track:after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${function(p) { return p.on ? '14px' : '2px'; }};
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #fff;
    transition: left 0.15s;
  }
`;

var HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

var Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;

  @media (max-width: 960px) {
    flex-direction: column;
    overflow: auto;
  }
`;

var Aside = styled.aside`
  width: 360px;
  flex-shrink: 0;
  overflow-y: auto;
  padding: 24px 24px 40px 28px;
  border-right: 1px solid ${C.line};

  @media (max-width: 960px) {
    width: 100%;
    border-right: 0;
    overflow: visible;
  }
`;

var Main = styled.main`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 24px 28px 48px;
  background: ${C.soft};

  @media (max-width: 960px) {
    overflow: visible;
  }
`;

/* ---------- Peças ---------- */

var Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 22px;
  padding: 0 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  color: ${function(p) { return p.color; }};
  background: ${function(p) { return p.color + '14'; }};
  box-shadow: inset 0 0 0 1px ${function(p) { return p.color + '33'; }};

  &:before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

var buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${C.line};
  background: #fff;
  color: ${C.text};
  transition: background 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s;
  text-decoration: none;

  &:hover {
    border-color: #d0d5dd;
    background: ${C.soft};
    color: ${C.ink};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${C.brandSoft};
    border-color: ${C.brand};
  }
`;

var Btn = styled.button`
  ${buttonBase}
  ${function(p) { return p.primary && css`
    background: ${C.brand};
    border-color: ${C.brand};
    color: #fff;
    &:hover { background: #1e40af; border-color: #1e40af; color: #fff; }
  `; }}
  ${function(p) { return p.small && css`
    height: 26px;
    padding: 0 9px;
    font-size: 12px;
    border-radius: 7px;
  `; }}
`;

var LinkBtn = styled.a`
  ${buttonBase}
`;

var IconBtn = styled.button`
  ${buttonBase}
  width: 32px;
  padding: 0;
  color: ${C.muted};
`;

var Journey = styled.ol`
  list-style: none;
  margin: 16px -4px 0;
  /* Folga para o anel dos círculos: sem ela o overflow corta o topo. */
  padding: 4px 4px 18px;
  display: grid;
  grid-template-columns: repeat(6, minmax(96px, 1fr));
  overflow-x: auto;
`;

var Step = styled.li`
  position: relative;
  padding-top: 28px;

  &:before {
    content: '';
    position: absolute;
    top: 9px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${function(p) { return p.passed ? C.ok : C.line; }};
  }

  &:first-child:before { left: 10px; }
  &:last-child:before { right: calc(100% - 10px); }
`;

var StepDot = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  background: ${function(p) { return p.done ? C.ok : '#fff'; }};
  color: ${function(p) { return p.done ? '#fff' : C.faint; }};
  box-shadow: 0 0 0 2px ${function(p) { return p.done ? C.ok : p.current ? C.brand : C.line; }};
`;

var StepLabel = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${function(p) { return p.done ? C.ink : p.current ? C.brand : C.faint; }};
`;

var StepDate = styled.div`
  font-size: 11px;
  color: ${C.muted};
  line-height: 16px;
`;

var Group = styled.section`
  & + & {
    margin-top: 26px;
  }
`;

var GroupTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${C.faint};
`;

var Field = styled.div`
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  gap: 12px;
  padding: 7px 0;
  border-bottom: 1px solid ${C.line};

  &:last-child { border-bottom: 0; }
`;

var FieldLabel = styled.div`
  color: ${C.muted};
`;

var FieldValue = styled.div`
  color: ${C.ink};
  font-weight: 500;
  word-break: break-word;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

var FieldSub = styled.div`
  color: ${C.muted};
  font-weight: 400;
  font-size: 12px;
`;

var Ghost = styled.button`
  border: 0;
  background: none;
  padding: 0 2px;
  color: ${C.faint};
  cursor: pointer;
  flex-shrink: 0;
  line-height: 1;
  &:hover { color: ${C.brand}; }
`;

var Empty = styled.span`
  color: ${C.faint};
  font-weight: 400;
`;

var CustomerName = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${C.ink};
  margin-bottom: 2px;
`;

var Recipient = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  margin: 0 -10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: ${C.soft}; }

  ${function(p) { return p.active && css`
    background: ${C.brandSoft};
    &:hover { background: ${C.brandSoft}; }
  `; }}
`;

var RecipientAddress = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${C.ink};
`;

var Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 4px;
`;

var Segmented = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  padding: 3px;
  background: #f2f4f7;
  border-radius: 9px;
`;

var Segment = styled.button`
  border: 0;
  height: 28px;
  padding: 0 12px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: ${function(p) { return p.active ? C.ink : C.muted; }};
  background: ${function(p) { return p.active ? '#fff' : 'transparent'; }};
  box-shadow: ${function(p) { return p.active ? '0 1px 2px rgba(16,24,40,.08)' : 'none'; }};
  transition: all 0.15s;

  &:hover { color: ${C.ink}; }

  small {
    margin-left: 6px;
    color: ${C.faint};
    font-weight: 500;
  }
`;

var Day = styled.div`
  position: sticky;
  top: -24px;
  z-index: 1;
  padding: 14px 0 8px;
  background: ${C.soft};
  font-size: 12px;
  font-weight: 600;
  color: ${C.muted};
`;

var Event = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  gap: 12px;
  padding-bottom: 10px;

  &:before {
    content: '';
    position: absolute;
    left: 17px;
    top: 36px;
    bottom: 0;
    width: 2px;
    background: ${C.line};
  }

  &:last-child:before { display: none; }
`;

var EventIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  color: ${function(p) { return p.color; }};
  background: #fff;
  box-shadow: inset 0 0 0 1px ${C.line}, 0 1px 2px rgba(16, 24, 40, 0.05);
`;

var EventCard = styled.div`
  background: #fff;
  border: 1px solid ${C.line};
  border-radius: 12px;
  padding: 10px 14px;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: #d0d5dd;
    box-shadow: 0 4px 10px -4px rgba(16, 24, 40, 0.1);
  }
`;

var EventHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

var EventTitle = styled.div`
  font-weight: 600;
  color: ${C.ink};
`;

var EventTime = styled.time`
  color: ${C.faint};
  font-size: 12px;
  white-space: nowrap;
`;

var EventMeta = styled.div`
  margin-top: 3px;
  display: flex;
  flex-wrap: wrap;
  gap: 2px 14px;
  color: ${C.muted};
  font-size: 12px;

  span, a {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
  }

  a {
    max-width: 340px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

var Failure = styled.div`
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${C.badSoft};
  color: #b42318;
  font-size: 12px;
  line-height: 18px;
  word-break: break-word;
`;

var EventActions = styled.div`
  margin-top: 10px;
  display: flex;
  gap: 8px;
`;

var Notice = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  margin: 10px 0 0;
  border-radius: 8px;
  background: ${C.brandSoft};
  color: ${C.brand};
  font-size: 12px;
`;

var ErrorBox = styled.div`
  padding: 16px;
  border-radius: 12px;
  background: ${C.badSoft};
  color: #b42318;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

var NothingHere = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${C.faint};
`;

var PreviewHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
`;

var PreviewCard = styled.div`
  background: #fff;
  border: 1px solid ${C.line};
  border-radius: 12px;
  padding: 20px;

  svg { font-size: 18px; }
`;

/* ---------- Formatação ---------- */

var capitalize = function(text) {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
};

var fullDate = function(date) {
  return moment(date).format('DD/MM/YYYY [às] HH:mm:ss');
};

var shortDateTime = function(date) {
  return moment(date).format('DD/MM/YYYY HH:mm');
};

var dateOnly = function(date) {
  return date ? moment(date).format('DD/MM/YYYY') : null;
};

var FieldRow = function(props) {
  var value = props.value;
  var empty = value === null || value === undefined || value === '';
  return (
    <Field>
      <FieldLabel>{props.label}</FieldLabel>
      <FieldValue>
        <span style={props.copy ? { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : undefined} title={props.copy && typeof value === 'string' ? value : undefined}>
          {empty ? <Empty>—</Empty> : value}
        </span>
        {!empty && props.copy && (
          <Tooltip title="Copiar">
            <Ghost type="button" aria-label={'Copiar ' + props.label} onClick={function() { copyToClipBoard(props.copy); }}>
              <Icon type="copy" />
            </Ghost>
          </Tooltip>
        )}
      </FieldValue>
    </Field>
  );
};

var WhenWho = function(props) {
  if (!props.date) return <Empty>—</Empty>;
  return (
    <span>
      <Tooltip title={fullDate(props.date)}>{shortDateTime(props.date)}</Tooltip>
      {props.who && props.who.name && <FieldSub>{props.who.name}</FieldSub>}
    </span>
  );
};

/**
 * Gaveta de detalhes do documento enviado.
 *
 * Cabeçalho com a jornada do documento (cadastrado → baixado). À esquerda,
 * tudo sobre o documento e o status de cada destinatário. À direita, a linha
 * do tempo de todos os eventos ou a prévia do e-mail como o cliente recebeu.
 *
 * Props: isOpen, closeModal, documentId; `document` (a linha da tabela) é
 * opcional: mostra o título enquanto carrega e é passado às ações.
 * Ações do cabeçalho, cada uma só aparece se a tela a oferecer:
 * onViewAsCustomer(linha), viewAsCustomerLoading, onSendWhatsapp(linha),
 * onSendEmails(id) e onArchive(id).
 */
export var DocumentDetailsDrawer = function(props) {
  var isOpen = props.isOpen;
  var documentId = props.documentId;
  var dispatch = useDispatch();

  var [isLoading, setIsLoading] = useState(false);
  var [error, setError] = useState(null);
  var [data, setData] = useState(null);
  var [kind, setKind] = useState('all');
  var [recipient, setRecipient] = useState(undefined);
  var [showTechnical, setShowTechnical] = useState(false);
  var [previewId, setPreviewId] = useState(null);
  var [resendModal, setResendModal] = useState({ isOpen: false, data: null });

  var load = useCallback(function() {
    if (!documentId) return Promise.resolve();
    setIsLoading(true);
    setError(null);
    return dispatch(documentsActions.loadDocumentTimeline(documentId))
      .then(function(response) {
        setData(response.payload.data.data);
      })
      .catch(function() {
        setError('Não foi possível carregar os detalhes do documento.');
      })
      .finally(function() {
        setIsLoading(false);
      });
  }, [documentId, dispatch]);

  useEffect(function() {
    if (isOpen && documentId) {
      setData(null);
      setKind('all');
      setRecipient(undefined);
      setPreviewId(null);
      load();
    }
  }, [isOpen, documentId, load]);

  var doc = data ? data.document : null;
  var emails = useMemo(function() { return data ? data.emails : []; }, [data]);
  var events = useMemo(function() { return data ? data.events : []; }, [data]);

  var headline = useMemo(function() { return getDocumentHeadline(doc, emails); }, [doc, emails]);
  var journey = useMemo(function() { return getDocumentJourney(doc, events); }, [doc, events]);
  var emailsById = useMemo(function() {
    var map = {};
    emails.forEach(function(email) { map[email.id] = email; });
    return map;
  }, [emails]);

  // O envio mais recente de cada destinatário.
  var latestByRecipient = useMemo(function() {
    var map = {};
    var order = [];
    emails.forEach(function(email) {
      var key = (email.email_to || '').toLowerCase();
      if (!map[key]) order.push(key);
      if (!map[key] || (email.created_at || '') >= (map[key].created_at || '')) map[key] = email;
    });
    return order.map(function(key) { return map[key]; });
  }, [emails]);

  // Passos técnicos ficam escondidos, a não ser que sejam o estado atual do
  // e-mail (ex.: preparado e nunca enviado), que precisa do "Reenviar".
  var visibleEvents = useMemo(function() {
    if (showTechnical) return events;
    return events.filter(function(event) {
      if (!isTechnicalEvent(event)) return true;
      var email = emailsById[event.email_log_id];
      var last = email && email.history.length ? email.history[email.history.length - 1] : null;
      return !!(last && last.date === event.date && last.event === event.event && !(event.event === 'clicked' && event.robot));
    });
  }, [events, emailsById, showTechnical]);
  var hiddenCount = events.length - visibleEvents.length;

  var groups = useMemo(function() {
    return groupEventsByDay(filterTimelineEvents(visibleEvents, kind, recipient), true);
  }, [visibleEvents, kind, recipient]);

  var situation = getDocumentSituation(doc);
  var emailCount = visibleEvents.filter(function(e) { return e.type === 'email'; }).length;
  var previewEmail = previewId ? emailsById[previewId] : null;
  var previewable = emails.filter(function(email) { return email.has_preview; });

  var openResend = function(email) {
    setResendModal({ isOpen: true, data: { emailTo: email.email_to, documentId: documentId } });
  };

  // Ações da linha da tabela: só aparecem quando a tela que abriu a gaveta as oferece.
  var row = props.document || null;
  var moreActions = [];
  if (row && props.onSendWhatsapp) {
    moreActions.push({ key: 'whatsapp', label: 'Enviar por WhatsApp', icon: 'message', run: function() { props.onSendWhatsapp(row); } });
  }
  if (props.onSendEmails && doc && !doc.archived_at) {
    moreActions.push({ key: 'emails', label: doc.email_status ? 'Enviar os e-mails de novo' : 'Enviar e-mails', icon: 'mail', run: function() { Promise.resolve(props.onSendEmails(documentId)).then(load); } });
  }
  if (props.onArchive && doc && !doc.archived_at) {
    moreActions.push({ key: 'archive', label: 'Arquivar', icon: 'inbox', danger: true, run: function() { props.onArchive(documentId); } });
  }

  /* ---------- Cabeçalho ---------- */

  var renderHeader = function() {
    var customer = doc && doc.customer;
    var emailMeta = doc && doc.email_status ? getEmailEventMeta(doc.email_status) : null;
    return (
      <Header>
        <HeaderTop>
          <FileTile><Icon type="file-text" /></FileTile>
          <TitleBlock>
            <Title>
              {doc ? doc.description : (props.document ? props.document.description : 'Documento')}
              {situation && <Pill color={situation.color}>{situation.label}</Pill>}
              {emailMeta && <Pill color={emailMeta.color}>{emailMeta.title}</Pill>}
            </Title>
            {doc && (
              <Subtitle>
                {customer && <span><Icon type="shop" />{(customer.cod ? customer.cod + ' · ' : '') + customer.name}</span>}
                {doc.due_at && <span><Icon type="calendar" />Vence {dateOnly(doc.due_at)}</span>}
                {doc.amount ? <span><Icon type="dollar" />{convertToBRL(doc.amount)}</span> : null}
                <span><Icon type="number" />{doc.id}</span>
              </Subtitle>
            )}
            {headline && (
              <Headline tone={HEADLINE_TONES[headline.tone]}>
                <Icon type={HEADLINE_TONES[headline.tone].icon} />
                {headline.parts.map(function(part, index) {
                  return (
                    <React.Fragment key={part}>
                      {index > 0 && <span className="sep">·</span>}
                      <span>{part}</span>
                    </React.Fragment>
                  );
                })}
              </Headline>
            )}
          </TitleBlock>
          <HeaderActions>
            {doc && doc.file_url && (
              <LinkBtn href={doc.file_url} target="_blank" rel="noopener noreferrer">
                <Icon type="export" />Abrir arquivo
              </LinkBtn>
            )}
            {row && props.onViewAsCustomer && (
              <Btn type="button" onClick={function() { props.onViewAsCustomer(row); }}>
                <Icon type={props.viewAsCustomerLoading ? 'loading' : 'user'} />Ver como cliente
              </Btn>
            )}
            {moreActions.length > 0 && (
              <Dropdown
                trigger={['click']}
                placement="bottomRight"
                overlay={
                  <Menu onClick={function(item) { moreActions.filter(function(a) { return a.key === item.key; })[0].run(); }}>
                    {moreActions.map(function(action) {
                      return (
                        <Menu.Item key={action.key} style={action.danger ? { color: C.bad } : undefined}>
                          <Icon type={action.icon} style={{ marginRight: 8 }} />{action.label}
                        </Menu.Item>
                      );
                    })}
                  </Menu>
                }
              >
                <Btn type="button">Mais ações<Icon type="down" /></Btn>
              </Dropdown>
            )}
            <Tooltip title="Atualizar">
              <IconBtn type="button" aria-label="Atualizar" onClick={load}>
                <Icon type={isLoading ? 'loading' : 'reload'} />
              </IconBtn>
            </Tooltip>
            <IconBtn type="button" aria-label="Fechar" onClick={props.closeModal}>
              <Icon type="close" />
            </IconBtn>
          </HeaderActions>
        </HeaderTop>

        {journey.length > 0 ? (
          <Journey>
            {journey.map(function(step) {
              return (
                <Step key={step.key} passed={step.done || step.skipped}>
                  <StepDot done={step.done} current={step.current}>
                    {step.done ? <Icon type="check" /> : step.skipped ? '–' : null}
                  </StepDot>
                  <StepLabel done={step.done} current={step.current}>{step.label}</StepLabel>
                  <StepDate>
                    {step.done ? (
                      <Tooltip title={fullDate(step.date) + (step.who && step.who.name ? ' · ' + step.who.name : '')}>
                        {shortDateTime(step.date)}
                      </Tooltip>
                    ) : step.skipped ? 'Não registrado' : step.current ? 'Aguardando' : '—'}
                  </StepDate>
                </Step>
              );
            })}
          </Journey>
        ) : (
          <div style={{ padding: '20px 0 6px' }}><Skeleton active title={false} paragraph={{ rows: 1 }} /></div>
        )}
      </Header>
    );
  };

  /* ---------- Coluna da esquerda ---------- */

  var renderAside = function() {
    if (!doc) return <Skeleton active paragraph={{ rows: 14 }} />;
    var customer = doc.customer || {};
    var task = doc.task;
    var channels = doc.delivery_methods || [];
    var channelDates = { 0: doc.email_sent_at, 1: doc.protocol_generated_at, 2: doc.panel_sent_at, 3: doc.whatsapp_sent_at };

    return (
      <div>
        <Group>
          <GroupTitle>Cliente</GroupTitle>
          <CustomerName>{(customer.cod ? customer.cod + ' · ' : '') + (customer.name || '—')}</CustomerName>
          <FieldRow label="CPF/CNPJ" value={formatIdentification(customer.identification_number)} copy={customer.identification_number} />
          <FieldRow label="E-mail" value={customer.email} copy={customer.email} />
          <FieldRow label="Telefone" value={formatPhone(customer.phone)} copy={customer.phone} />
        </Group>

        {latestByRecipient.length > 0 && (
          <Group>
            <GroupTitle>Destinatários ({latestByRecipient.length})</GroupTitle>
            {latestByRecipient.map(function(email) {
              var meta = getEmailEventMeta(email.event);
              var active = (!previewId && recipient === email.email_to) || previewId === email.id;
              return (
                <Recipient
                  key={email.id}
                  active={active}
                  onClick={function() { setPreviewId(null); setKind('all'); setRecipient(email.email_to); }}
                  title="Ver só os eventos deste destinatário"
                >
                  <RecipientAddress>{email.email_to}</RecipientAddress>
                  <Pill color={meta.color}>{meta.title.replace('E-mail ', '')}</Pill>
                  {email.has_preview && (
                    <Tooltip title="Ver o e-mail enviado">
                      <Ghost type="button" aria-label="Ver o e-mail enviado" onClick={function(e) { e.stopPropagation(); setPreviewId(email.id); }}>
                        <Icon type="eye" />
                      </Ghost>
                    </Tooltip>
                  )}
                </Recipient>
              );
            })}
          </Group>
        )}

        <Group>
          <GroupTitle>Documento</GroupTitle>
          {doc.type && <FieldRow label="Tipo" value={doc.type} />}
          <FieldRow label="Departamento" value={department(doc.department) || doc.department || null} />
          <FieldRow label="Competência" value={doc.competency_at ? moment(doc.competency_at).format('MM/YYYY') : null} />
          <FieldRow label="Vencimento" value={dateOnly(doc.due_at)} />
          <FieldRow label="Valor" value={doc.amount ? convertToBRL(doc.amount) : null} />
          <FieldRow
            label="Enviado por"
            value={channels.length === 0 ? null : (
              <span>
                {channels.map(function(method, index) {
                  var name = (deliveryMethodNames[method] || 'Canal ' + method) + (method === 1 && doc.protocol ? ' #' + doc.protocol.id : '');
                  return (
                    <React.Fragment key={method}>
                      {index > 0 && ', '}
                      {channelDates[method]
                        ? <Tooltip title={fullDate(channelDates[method])}>{name}</Tooltip>
                        : name}
                    </React.Fragment>
                  );
                })}
              </span>
            )}
          />
          <FieldRow label="Arquivo" value={doc.filename} copy={doc.file_url} />
        </Group>

        <Group>
          <GroupTitle>Rastreabilidade</GroupTitle>
          {task && <FieldRow label="Tarefa" value={<span>{task.name}<FieldSub>Prazo {dateOnly(task.due_date)}</FieldSub></span>} />}
          {task && <Field><FieldLabel>Executada</FieldLabel><FieldValue><WhenWho date={task.executed_at} who={task.executed_by} /></FieldValue></Field>}
          <Field><FieldLabel>Cadastrado</FieldLabel><FieldValue><WhenWho date={doc.created_at} who={doc.created_by} /></FieldValue></Field>
          <Field><FieldLabel>Revisado</FieldLabel><FieldValue><WhenWho date={doc.revised_at} who={doc.revised_by} /></FieldValue></Field>
          <Field><FieldLabel>Baixado</FieldLabel><FieldValue><WhenWho date={doc.viewed_at} who={doc.viewed_by} /></FieldValue></Field>
          {doc.archived_at && <Field><FieldLabel>Arquivado</FieldLabel><FieldValue><WhenWho date={doc.archived_at} who={doc.archived_by} /></FieldValue></Field>}
          {doc.archived_at && <FieldRow label="Motivo" value={doc.reason_to_archive} />}
          {doc.deleted_at && <Field><FieldLabel>Excluído</FieldLabel><FieldValue><WhenWho date={doc.deleted_at} who={doc.deleted_by} /></FieldValue></Field>}
          <FieldRow label="Código" value={'#' + doc.id} copy={String(doc.id)} />
        </Group>
      </div>
    );
  };

  /* ---------- Coluna da direita: linha do tempo ---------- */

  var renderEvent = function(event, index) {
    var meta = getTimelineEventMeta(event);
    var email = event.email_log_id ? emailsById[event.email_log_id] : null;
    var last = email && email.history.length ? email.history[email.history.length - 1] : null;
    var isLatestOfEmail = !!(last && last.date === event.date && last.event === event.event);
    var canResend = isLatestOfEmail && RESENDABLE.indexOf(email.event) !== -1;
    var canPreview = !!(email && email.has_preview) && (event.event === 'draft' || isLatestOfEmail);

    return (
      <Event key={event.type + event.date + index}>
        <EventIcon color={meta.color}><Icon type={meta.icon} /></EventIcon>
        <EventCard>
          <EventHead>
            <EventTitle>{meta.title}</EventTitle>
            <Tooltip title={fullDate(event.date) + ' · ' + moment(event.date).fromNow()}>
              <EventTime dateTime={event.date}>{moment(event.date).format('HH:mm')}</EventTime>
            </Tooltip>
          </EventHead>
          <EventMeta>
            {event.email_to && <span><Icon type="mail" />{event.email_to}</span>}
            {event.actor && event.actor.name && <span><Icon type="user" />{event.actor.name}</span>}
            {email && email.subject && event.event === 'draft' && <span><Icon type="message" />{email.subject}</span>}
            {showTechnical && event.gateway && <span style={{ color: C.faint }}>via {getGatewayLabel(event.gateway)}</span>}
            {event.url && <a href={event.url} target="_blank" rel="noopener noreferrer"><Icon type="link" />{event.url}</a>}
            {event.type !== 'email' && event.detail && <span>{event.detail}</span>}
          </EventMeta>
          {event.type === 'email' && event.detail && <Failure><Icon type="info-circle" style={{ marginRight: 6 }} />{event.detail}</Failure>}
          {(canPreview || canResend) && (
            <EventActions>
              {canPreview && (
                <Btn small type="button" onClick={function() { setPreviewId(email.id); }}>
                  <Icon type="eye" />Ver e-mail
                </Btn>
              )}
              {canResend && (
                <Btn small primary type="button" onClick={function() { openResend(email); }}>
                  <Icon type="redo" />Reenviar
                </Btn>
              )}
            </EventActions>
          )}
        </EventCard>
      </Event>
    );
  };

  var renderTimeline = function() {
    return (
      <div>
        <Toolbar>
          <Segmented role="tablist">
            <Segment type="button" active={kind === 'all'} onClick={function() { setKind('all'); }}>Tudo<small>{visibleEvents.length}</small></Segment>
            <Segment type="button" active={kind === 'emails'} onClick={function() { setKind('emails'); }}>E-mails<small>{emailCount}</small></Segment>
            <Segment type="button" active={kind === 'document'} onClick={function() { setKind('document'); }}>Documento<small>{visibleEvents.length - emailCount}</small></Segment>
          </Segmented>
          <Switch type="button" role="switch" aria-checked={showTechnical} on={showTechnical} onClick={function() { setShowTechnical(!showTechnical); }}>
            <i className="track" />
            Mostrar detalhes técnicos{!showTechnical && hiddenCount > 0 ? ' (' + hiddenCount + ')' : ''}
          </Switch>
        </Toolbar>

        {recipient && (
          <Notice>
            <span>Mostrando só os eventos de <strong>{recipient}</strong></span>
            <Btn small type="button" onClick={function() { setRecipient(undefined); }}>Ver todos</Btn>
          </Notice>
        )}

        {groups.length === 0 ? (
          <NothingHere>Nenhum evento para estes filtros</NothingHere>
        ) : groups.map(function(group) {
          return (
            <div key={group.day}>
              <Day>{capitalize(moment(group.day).format('dddd, DD [de] MMMM [de] YYYY'))}</Day>
              {group.events.map(renderEvent)}
            </div>
          );
        })}
      </div>
    );
  };

  /* ---------- Coluna da direita: prévia do e-mail ---------- */

  var renderPreview = function() {
    var email = previewEmail;
    var meta = getEmailEventMeta(email.event);
    return (
      <div>
        <PreviewHead>
          <Btn type="button" onClick={function() { setPreviewId(null); }}>
            <Icon type="arrow-left" />Linha do tempo
          </Btn>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Pill color={meta.color}>{meta.title}</Pill>
            {RESENDABLE.indexOf(email.event) !== -1 && (
              <Btn small primary type="button" onClick={function() { openResend(email); }}><Icon type="redo" />Reenviar</Btn>
            )}
          </div>
        </PreviewHead>

        {previewable.length > 1 && (
          <div style={{ marginBottom: 14 }}>
            <Segmented>
              {previewable.map(function(item) {
                return (
                  <Segment key={item.id} type="button" active={item.id === email.id} onClick={function() { setPreviewId(item.id); }}>
                    {item.email_to}
                  </Segment>
                );
              })}
            </Segmented>
          </div>
        )}

        <PreviewCard>
          <EmailPreview
            key={email.id}
            logId={email.id}
            iframeWidth="100%"
            iframeHeight={640}
            iframeStyle={{ border: 0, borderRadius: 8, background: '#fff' }}
            data={{ title: email.subject, email_from: email.email_from, email: email.email_to, sent_at: email.created_at }}
          />
        </PreviewCard>
      </div>
    );
  };

  var renderMain = function() {
    if (isLoading && !data) {
      return [1, 2, 3, 4].map(function(i) { return <Skeleton key={i} active avatar={{ shape: 'square' }} paragraph={{ rows: 1 }} />; });
    }
    if (error) {
      return (
        <ErrorBox>
          <span><Icon type="warning" style={{ marginRight: 8 }} />{error}</span>
          <Btn small type="button" onClick={load}>Tentar de novo</Btn>
        </ErrorBox>
      );
    }
    return previewEmail ? renderPreview() : renderTimeline();
  };

  return (
    <Drawer
      visible={isOpen}
      onClose={props.closeModal}
      closable={false}
      width="min(1240px, 96vw)"
      destroyOnClose
      bodyStyle={{ padding: 0, height: '100%' }}
    >
      <Shell>
        {renderHeader()}
        <Body>
          <Aside>{renderAside()}</Aside>
          <Main>{renderMain()}</Main>
        </Body>
      </Shell>
      <ResendEmailModal
        isOpen={resendModal.isOpen}
        closeModal={function() { setResendModal({ isOpen: false, data: null }); }}
        data={resendModal.data}
        loadLogs={load}
      />
    </Drawer>
  );
};

export default DocumentDetailsDrawer;
